/**
 * Pure, React-free helpers for the Service Directory domain: id generation,
 * blank/edit draft factories, list parsing, validation, filtering, sorting,
 * formatting, and the CLIENT-SIDE "who knows this system?" expert derivation.
 *
 * Deliberately NO productivity/velocity/leaderboard metric: the expert
 * derivation surfaces STARTING POINTS of knowledge (roles relative to the
 * service), never a ranking or score of people.
 */

import { v4 as uuid } from 'uuid';
import type {
  Service,
  ServiceDraft,
  ServiceExpert,
  ServiceFilter,
  ServiceSummary,
} from './types';
import type { TeamDocumentSummary } from '../documents/types';
import type { DecisionSummary } from '../decisions/types';

/** Deterministic document id for a new service. */
export function serviceDocId(): string {
  return `svc_${uuid()}`;
}

/** Creates an empty draft for the register form (optionally seeding fields). */
export function blankServiceDraft(
  primaryPoc = '',
  seed: Partial<ServiceDraft> = {},
): ServiceDraft {
  return {
    name: '',
    purpose: '',
    owningTeam: '',
    primaryPoc,
    secondaryPoc: undefined,
    repositories: [],
    currentGoalIds: [],
    activeWorkstreamIds: [],
    documentIds: [],
    recentDecisionIds: [],
    knownDependencies: [],
    ...seed,
  };
}

/** Projects an existing service into an editable draft. */
export function draftFromService(service: Service): ServiceDraft {
  return {
    name: service.name,
    purpose: service.purpose,
    owningTeam: service.owningTeam,
    primaryPoc: service.primaryPoc,
    secondaryPoc: service.secondaryPoc,
    repositories: service.repositories ?? [],
    currentGoalIds: service.currentGoalIds ?? [],
    activeWorkstreamIds: service.activeWorkstreamIds ?? [],
    documentIds: service.documentIds ?? [],
    recentDecisionIds: service.recentDecisionIds ?? [],
    knownDependencies: service.knownDependencies ?? [],
  };
}

/**
 * Parses a comma / newline separated string into a normalized, de-duped list
 * (trimmed, empties dropped, original order preserved).
 */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (raw ?? '').split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins a list back into a comma-separated string for an input field. */
export function listToInput(list?: string[]): string {
  return (list ?? []).join(', ');
}

/** Validates a draft for save. A service needs a name, a purpose, and a primary POC. */
export function validateServiceDraft(draft: ServiceDraft): string[] {
  const problems: string[] = [];
  if (!draft.name.trim()) problems.push('A service name is required.');
  if (!draft.purpose.trim()) {
    problems.push('A purpose is required — it answers "what is this system for?".');
  }
  if (!draft.primaryPoc.trim()) {
    problems.push('A primary POC is required — it answers "who do I start with?".');
  }
  return problems;
}

/** Unique, sorted set of owning teams present across the given services. */
export function collectTeams(items: ServiceSummary[]): string[] {
  const set = new Set<string>();
  for (const s of items) {
    const t = (s.owningTeam ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Unique, sorted set of POC aliases (primary + secondary) across the services. */
export function collectPocs(items: ServiceSummary[]): string[] {
  const set = new Set<string>();
  for (const s of items) {
    const p = (s.primaryPoc ?? '').trim();
    if (p) set.add(p);
    const q = (s.secondaryPoc ?? '').trim();
    if (q) set.add(q);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Applies the structured filters + free-text query (empty fields match everything). */
export function filterServices(
  items: ServiceSummary[],
  filter: ServiceFilter,
): ServiceSummary[] {
  const team = filter.owningTeam.trim();
  const poc = filter.poc.trim().toLowerCase();
  const query = filter.query.trim().toLowerCase();
  return items.filter((s) => {
    if (team && (s.owningTeam ?? '').toLowerCase() !== team.toLowerCase()) return false;
    if (
      poc &&
      (s.primaryPoc ?? '').toLowerCase() !== poc &&
      (s.secondaryPoc ?? '').toLowerCase() !== poc
    ) {
      return false;
    }
    if (query) {
      const hay = [
        s.name,
        s.purpose,
        s.owningTeam,
        ...(s.repositories ?? []),
        ...(s.knownDependencies ?? []),
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(query)) return false;
    }
    return true;
  });
}

/** Sorts services alphabetically by name. */
export function sortServices(items: ServiceSummary[]): ServiceSummary[] {
  return items
    .slice()
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
}

/** Adds a role to the expert accumulator, keyed case-insensitively by `who`. */
function addExpert(
  acc: Map<string, ServiceExpert>,
  who: string | undefined,
  role: string,
  isTeam = false,
): void {
  const key = (who ?? '').trim();
  if (!key) return;
  const mapKey = `${isTeam ? 'team:' : 'user:'}${key.toLowerCase()}`;
  const existing = acc.get(mapKey);
  if (existing) {
    if (!existing.roles.includes(role)) existing.roles.push(role);
    return;
  }
  acc.set(mapKey, { who: key, roles: [role], isTeam });
}

/**
 * Derives "who knows this system?" for a service, CLIENT-SIDE, by folding
 * together its declared owners + the authors/owners of the documents and
 * decisions linked to it. Each returned entry lists the distinct roles that
 * surfaced the person/team — a starting point for a conversation, never a
 * ranking. Ordered: individuals before teams, then alphabetically.
 */
export function deriveServiceExperts(
  service: Pick<
    ServiceSummary,
    'owningTeam' | 'primaryPoc' | 'secondaryPoc' | 'documentIds' | 'recentDecisionIds'
  >,
  documents: TeamDocumentSummary[],
  decisions: DecisionSummary[],
): ServiceExpert[] {
  const acc = new Map<string, ServiceExpert>();
  addExpert(acc, service.primaryPoc, 'Primary POC');
  addExpert(acc, service.secondaryPoc, 'Secondary POC');
  addExpert(acc, service.owningTeam, 'Owning team', true);

  const docIds = new Set(service.documentIds ?? []);
  for (const doc of documents) {
    if (!docIds.has(doc.id)) continue;
    addExpert(acc, doc.ownerPoc, 'Doc owner');
    for (const a of doc.authors ?? []) addExpert(acc, a, 'Doc author');
  }

  const decisionIds = new Set(service.recentDecisionIds ?? []);
  for (const dec of decisions) {
    if (!decisionIds.has(dec.id)) continue;
    addExpert(acc, dec.owner, 'Decision owner');
    for (const p of dec.participants ?? []) addExpert(acc, p, 'Decision participant');
  }

  return Array.from(acc.values()).sort((a, b) => {
    if (!!a.isTeam !== !!b.isTeam) return a.isTeam ? 1 : -1;
    return a.who.localeCompare(b.who);
  });
}

/** Formats an ISO timestamp as a compact local date. */
export function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
